import React, { useState } from 'react';
import { F } from '../theme.jsx';
import { useModalViewport, modalShellStyle, ModalHeader } from '../modal-utils.jsx';
import { Portal } from '../components/boothly/portal.jsx';
import { Z } from '../lib/z-index.js';
import { useToast } from '../components/boothly/toast-provider.jsx';

// ⚠ PLATZHALTER — VOR LAUNCH durch juristisch geprüfte Texte ersetzen!
export const PRIVACY_TEXT = `⚠ PLATZHALTER — KEIN RECHTSGÜLTIGER TEXT ⚠
Dieser Text wird vor dem offiziellen Launch durch eine vollständige, juristisch geprüfte Datenschutzerklärung ersetzt.

1. Verantwortlicher
Boothly · office@memorica.de

2. Erhobene Daten
• Account-Daten (E-Mail, Passwort-Hash)
• Bereichs- und Artikeldaten (Stores, Items, Sessions, Verkäufe)
• Technische Logs (Fehler, Audit-Trail für Datensicherheit)

3. Zweck der Verarbeitung
Ausschließlich zur Bereitstellung der App-Funktionen.

4. Speicherort
EU-Rechenzentren (Supabase/AWS Frankfurt, Cloudflare EU).

5. Speicherdauer
Bis zur Konto-Löschung. Danach sofortige und vollständige Löschung.

6. Weitergabe
Keine Weitergabe an Dritte außer technisch notwendige Cloud-Dienstleister (Auftragsverarbeiter).

7. Deine Rechte (DSGVO Art. 15–21)
• Auskunft über gespeicherte Daten (Einstellungen → Datenschutz → Daten exportieren)
• Berichtigung, Löschung, Einschränkung der Verarbeitung
• Widerspruchsrecht, Recht auf Datenübertragbarkeit
• Beschwerde bei der zuständigen Aufsichtsbehörde

8. Kontakt
office@memorica.de

Stand: 2026 (Platzhalter)`;

export const AGB_TEXT = `⚠ PLATZHALTER — KEIN RECHTSGÜLTIGER TEXT ⚠
Diese AGB werden vor dem offiziellen Launch durch vollständige, juristisch geprüfte Allgemeine Geschäftsbedingungen ersetzt.

§ 1 Geltungsbereich
Diese Nutzungsbedingungen gelten für die Nutzung der Boothly-App.

§ 2 Leistungsumfang
Boothly stellt eine App zur Verwaltung von Verkaufsständen, Flohmarkt-Artikeln und Markt-Sessions bereit.

§ 3 Nutzerkonto
Die Registrierung erfordert eine gültige E-Mail-Adresse. Der Nutzer ist für die Sicherheit seines Passworts verantwortlich.

§ 4 Haftung
Boothly übernimmt keine Haftung für Datenverluste. Regelmäßige Exports werden empfohlen (Einstellungen → Datenschutz).

§ 5 Kündigung
Der Nutzer kann sein Konto jederzeit selbst löschen (Einstellungen → Konto löschen). Alle Daten werden dabei sofort und unwiderruflich gelöscht.

§ 6 Kontakt
Boothly · office@memorica.de

Stand: 2026 (Platzhalter)`;

// ─────────────────────────────────────────────────────────────
// PrivacySheet — DSGVO-Dashboard (Datenschutz & AGB)
// ─────────────────────────────────────────────────────────────
export function PrivacySheet({ dark, t, authToken, onClose }) {
  const toast = useToast();
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [exporting, setExporting] = useState(false);
  const [dseOpen, setDseOpen] = useState(false);
  const [agbOpen, setAgbOpen] = useState(false);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await fetch("/api/me/export", {
        headers: { "Authorization": `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error("Export fehlgeschlagen");
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const fnMatch = cd.match(/filename="([^"]+)"/);
      const fileName = fnMatch ? fnMatch[1] : `memorica-export-${new Date().toISOString().slice(0, 10)}.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fileName; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Daten exportiert");
    } catch (e) {
      toast.error(e.message || "Export fehlgeschlagen");
    } finally {
      setExporting(false);
    }
  };

  const infoRows = [
    { label: "Was wird gespeichert?", text: "Account-Daten (E-Mail), deine Bereiche, Artikel, Sitzungen und Verkaufshistorie." },
    { label: "Wo werden Daten gespeichert?", text: "Ausschließlich in EU-Rechenzentren (Supabase/AWS Frankfurt, Cloudflare EU)." },
    { label: "Wie lange?", text: "Bis zur Konto-Löschung. Nach Löschung werden alle personenbezogenen Daten sofort entfernt." },
    { label: "Weitergabe?", text: "Keine Weitergabe an Dritte, außer rein technisch notwendige Cloud-Infrastruktur." },
  ];

  return (
    <Portal>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: Z.nestedBackdrop,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 480, radius: 24, padding: 0, zIndex: Z.nestedSheet })}>
        <div style={{ padding: "20px 22px 12px" }}>
          <ModalHeader t={t} dark={dark} F={F} title="Datenschutz & AGB" onClose={onClose} marginBottom={8} />
        </div>
        <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "0 22px calc(env(safe-area-inset-bottom,14px) + 20px)", maxHeight: Math.max(280, vh - 140) }}>

          {/* Info-Kacheln */}
          <div style={{ marginBottom: 18 }}>
            {infoRows.map((row, i) => (
              <div key={i} style={{
                background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                borderRadius: 11, padding: "10px 13px",
                marginBottom: 6, border: `1px solid ${t.glassBorder}`,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 3 }}>{row.label}</div>
                <div style={{ fontSize: 12, color: t.textSub, lineHeight: 1.5 }}>{row.text}</div>
              </div>
            ))}
          </div>

          {/* Daten-Export */}
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Deine Daten</div>
          <div style={{ background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)", borderRadius: 13, overflow: "hidden", border: `1px solid ${t.glassBorder}`, marginBottom: 18 }}>
            <button onClick={handleExport} disabled={exporting} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              width: "100%", padding: "14px 16px",
              background: "transparent", border: "none",
              fontFamily: F, cursor: exporting ? "default" : "pointer",
              borderBottom: `0.5px solid ${t.divider}`,
            }}>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: t.text }}>{exporting ? "Exportiere…" : "Daten exportieren"}</div>
                <div style={{ fontSize: 11, color: t.textSub, marginTop: 2 }}>JSON-Datei mit allen deinen Daten</div>
              </div>
              <span style={{ fontSize: 18, color: t.textMuted }}>↓</span>
            </button>
            <button onClick={() => setDseOpen(true)} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              width: "100%", padding: "14px 16px",
              background: "transparent", border: "none",
              fontFamily: F, cursor: "pointer",
              borderBottom: `0.5px solid ${t.divider}`,
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.text, textAlign: "left" }}>Datenschutzerklärung</div>
              <span style={{ fontSize: 15, color: t.textMuted }}>›</span>
            </button>
            <button onClick={() => setAgbOpen(true)} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              width: "100%", padding: "14px 16px",
              background: "transparent", border: "none",
              fontFamily: F, cursor: "pointer",
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.text, textAlign: "left" }}>AGB</div>
              <span style={{ fontSize: 15, color: t.textMuted }}>›</span>
            </button>
          </div>

          {/* Kontakt */}
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Kontakt</div>
          <div style={{ background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)", borderRadius: 13, overflow: "hidden", border: `1px solid ${t.glassBorder}`, marginBottom: 8 }}>
            <button onClick={() => { window.location.href = "mailto:office@memorica.de?subject=Datenschutz-Anfrage"; }} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              width: "100%", padding: "14px 16px",
              background: "transparent", border: "none",
              fontFamily: F, cursor: "pointer",
            }}>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: t.text }}>Datenschutz-Anfrage</div>
                <div style={{ fontSize: 11, color: t.textSub, marginTop: 2 }}>office@memorica.de</div>
              </div>
              <span style={{ fontSize: 15, color: t.textMuted }}>✉</span>
            </button>
          </div>

          <div style={{ fontSize: 11, color: t.textSub, lineHeight: 1.5, paddingTop: 4 }}>
            ⚠ Datenschutzerklärung und AGB sind Platzhalter und werden vor dem offiziellen Launch durch juristisch geprüfte Texte ersetzt.
          </div>
        </div>
      </div>

      {dseOpen && <StaticTextSheet dark={dark} t={t} title="Datenschutzerklärung" body={PRIVACY_TEXT} onClose={() => setDseOpen(false)} />}
      {agbOpen && <StaticTextSheet dark={dark} t={t} title="AGB" body={AGB_TEXT} onClose={() => setAgbOpen(false)} />}
    </Portal>
  );
}

// ─────────────────────────────────────────────────────────────
// StaticTextSheet — generisches Modal für AGB / Datenschutz / FAQ
// ─────────────────────────────────────────────────────────────
export function StaticTextSheet({ dark, t, title, body, onClose }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  return (
    <Portal>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: Z.nestedBackdrop,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 480, radius: 24, padding: 0, zIndex: Z.nestedSheet })}>
        <div style={{ padding: "20px 22px 12px" }}>
          <ModalHeader t={t} dark={dark} F={F} title={title} onClose={onClose} marginBottom={8} />
        </div>
        <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "0 22px 22px", maxHeight: Math.max(280, vh - 140), fontSize: 13, color: t.text, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
          {body}
        </div>
      </div>
    </Portal>
  );
}
